// Road route via the free OSRM demo server (no API key). Falls back to null.
const cache = {};

export async function getRoute(origin, destination) {
  if (!origin || !destination) return null;
  if (origin.latitude == null || destination.latitude == null) return null;
  const key = `${origin.latitude.toFixed(4)},${origin.longitude.toFixed(4)};${destination.latitude.toFixed(4)},${destination.longitude.toFixed(4)}`;
  if (cache[key]) return cache[key];
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${origin.longitude},${origin.latitude};${destination.longitude},${destination.latitude}?overview=full&geometries=geojson`;
    const r = await fetch(url);
    const d = await r.json();
    if (d.code === 'Ok' && d.routes && d.routes[0]) {
      const coords = d.routes[0].geometry.coordinates.map(([lng, lat]) => ({ latitude: lat, longitude: lng }));
      const info = { coords, distance: d.routes[0].distance, duration: d.routes[0].duration };
      cache[key] = info;
      return info;
    }
  } catch (e) {
    // routing service unavailable — caller draws a straight fallback line
    console.warn('OSRM route lookup failed; using straight-line fallback:', e);
  }
  return null;
}

function haversine(a, b) {
  const R = 6371000;
  const dLat = ((b.latitude - a.latitude) * Math.PI) / 180;
  const dLng = ((b.longitude - a.longitude) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos((a.latitude * Math.PI) / 180) * Math.cos((b.latitude * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// Straight-line distance in metres between two {latitude, longitude} points.
export function distanceMeters(a, b) {
  if (!a || !b || a.latitude == null || b.latitude == null) return null;
  return haversine(a, b);
}

// Shortest distance (metres) from a position to any point on the planned route.
// Used to detect when a ride has strayed well off the expected path.
export function distanceToRoute(routeInfo, pos) {
  if (!routeInfo || !routeInfo.coords || !routeInfo.coords.length || !pos || pos.latitude == null) return null;
  let best = Infinity;
  for (const pt of routeInfo.coords) {
    const d = haversine(pt, pos);
    if (d < best) best = d;
  }
  return best === Infinity ? null : best;
}

// Estimate minutes remaining, using the route's total duration scaled by how much
// of the route distance is still ahead of the current position. No extra API calls.
export function remainingMinutes(routeInfo, pos) {
  if (!routeInfo || !routeInfo.coords || routeInfo.coords.length < 2 || !pos) return null;
  const coords = routeInfo.coords;
  // nearest route point to current position
  let nearest = 0, best = Infinity;
  for (let i = 0; i < coords.length; i++) {
    const d = haversine(coords[i], pos);
    if (d < best) { best = d; nearest = i; }
  }
  // remaining distance along route from nearest point to end
  let remaining = 0;
  for (let i = nearest; i < coords.length - 1; i++) remaining += haversine(coords[i], coords[i + 1]);
  const total = routeInfo.distance || 1;
  const dur = routeInfo.duration || 0; // seconds
  if (dur <= 0 || total <= 0) return null;
  const mins = Math.round((dur * (remaining / total)) / 60);
  return mins;
}
